/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import angular from 'angular';
import keyboard from 'keycode-ui';
import mime from 'mime-types';
import _ from 'lodash';
/*---------------------------------------------------------*\
 * constants
\*---------------------------------------------------------*/
const deps = {};
/*---------------------------------------------------------*\
 * VideoplayerController
\*---------------------------------------------------------*/
class VideoplayerController {
  /* ----------------------------------- *\
   * Constructor
  \* ----------------------------------- */
  constructor($rootScope, $scope, $sce, $timeout, $state, $interval, $stateParams, states, navigationService,
              localService, topics, storageKeys, adRotationFrequency) {
    deps.$rootScope = $rootScope;
    deps.$scope = $scope;
    deps.$state = $state;
    deps.states = states;
    deps.$timeout = $timeout;
    deps.$interval = $interval;
    deps.navigationService = navigationService;
    deps.topics = topics;
    deps.$stateParams = $stateParams;
    deps.localService = localService;
    deps.storageKeys = storageKeys;
    deps.adRotationFrequency = adRotationFrequency;

    this.name = 'videoplayer';
    this.currentPlayerState = 'Stop';
    this.API = null;
    this.lockControls = false;
    // Ad and Preroll related params
    this.isAd = deps.$stateParams.ad !== undefined;
    this.playPreroll = this.shouldPlayPreroll();
    const selectedRegion = deps.localService.get(deps.storageKeys.selectedRegion);
    this.region = selectedRegion ? selectedRegion.abbr : 'LI';
    this.category = deps.$stateParams.category || 'news';
    if (this.isAd) {
      this.videoSource = deps.$stateParams.ad ? deps.$stateParams.ad.src : null;
    } else {
      this.videoSource = deps.$stateParams.currentStory ? deps.$stateParams.currentStory.story.videoUrl : null;
    }

    if (!this.playPreroll && !this.isAd) {
      // Preroll in not going to play, but we still need to increment video play count
      this.incrementPrerollCount();
    }

    deps.$timeout(() => {
      let mimeType;
      // amazon fire TV workaround for MPG files. Currently MPG videos do not play on firetv.
      // if the mimetype is set to video/mpeg.
      // therefore we are forcefully faking it to use .mp4 when the extension is mpg.
      if (_.contains(this.videoSource, '.mpg')) {
        mimeType = 'video/mp4';
      } else {
        mimeType = mime.lookup(this.videoSource);
      }
      this.config = {
        autohide: true,
        autohideTime: 1000,
        playsInline: false,
        loop: false,
        preload: 'none',
        sources: [
          {
            src: $sce.trustAsResourceUrl(this.videoSource),
            type: mimeType
          }
        ],
        theme: './../../../assets/videogular/videogular.css'
      };
    });

    deps.$scope.$on('$destroy', () => {
      deps.$interval.cancel(this.promise);
    });
  }

  $postLink() {
    deps.navigationService.focusElAsync(this.name);
  }

  get prerollCount() {
    return deps.localService.get(deps.storageKeys.adPlayCount) || 0;
  }
  set prerollCount(count) {
    deps.localService.set(deps.storageKeys.adPlayCount, count);
  }

  shouldPlayPreroll() {
    // determine if preroll ad should play
    if (this.isAd) {
      // don't preroll on top of video ads
      return false;
    }
    return this.prerollCount === 0;
  }

  incrementPrerollCount() {
    this.prerollCount = (this.prerollCount + 1) % deps.adRotationFrequency;
  }

  onPlayerReady(API) {
    this.API = API;
    this.API.playsInline = true;
    this.API.setPlayback(1);
  }

  timeoutPlay() {
    deps.$timeout(() => {
      this.API.toggleFullScreen();
      this.API.play();
      this.currentStatus = deps.topics.playerStates.play.name;
    });
  }

  seekRepeat(option, scroll) {
    const totalTime = this.API.totalTime / 1000;
    const currentTime = this.API.currentTime / 1000;
    const frameTime = (totalTime / 100) * 3;

    if (option === deps.topics.playerStates.forward.name) {
      if (totalTime >= (currentTime + frameTime)) {
        const scrollSpeed = scroll ? 1 : 2;
        this.API.pause();
        this.API.seekTime(currentTime + frameTime);
        this.currentStatus = deps.topics.playerStates.forward.name;
        this.API.setPlayback(scrollSpeed);
      } else {
        this.API.seekTime(totalTime);
        deps.$interval.cancel(this.promise);
        this.currentStatus = deps.topics.playerStates.play.name;
        this.goBack();
      }
    } else if (option === deps.topics.playerStates.rewind.name) {
      if ((currentTime - 3) > 0) {
        const scrollSpeed = scroll ? 1 : -2;
        this.API.seekTime(currentTime - frameTime);
        this.currentStatus = deps.topics.playerStates.rewind.name;
        this.API.setPlayback(scrollSpeed);
      } else {
        deps.$interval.cancel(this.promise);
        this.API.stop();
        this.currentStatus = this.API.currentState;
        this.API.setPlayback(1);
        this.API.seekTime(0);
        deps.$timeout(() => {
          this.API.play();
          this.currentStatus = deps.topics.playerStates.play.name;
        });
      }
    }
  }

  remoteKey(e) {
    const ignoreVideoControls = this.lockControls || this.isAd;
    if (angular.isDefined(this.promise)) {
      deps.$interval.cancel(this.promise);
    }
    switch (true) {
      // BACK BUTTON
      case keyboard.isBack(e):
        // removing this due to android webview issue. Once we have ability to
        // handle android webview back we can reenable this
        // e.preventDefault();
        break;
      // UP / DOWN
      case (keyboard.isDown(e) || keyboard.isUp(e)):
        if (!ignoreVideoControls) {
          deps.navigationService.focusElAsync('.controls-container');
        }
        break;
      // PLAY / PAUSE
      case keyboard.isPlayPause(e):
        if (!ignoreVideoControls) {
          if (this.currentStatus === deps.topics.playerStates.play.name) {
            this.API.playPause();
          } else {
            this.API.play();
          }
          this.currentStatus = deps.topics.playerStates.play.name;
          this.API.setPlayback(1);
        }
        break;
      // RWD
      case keyboard.isRewind(e):
        if (!ignoreVideoControls) {
          this.promise = deps.$interval(() => {
            this.seekRepeat(deps.topics.playerStates.rewind.name);
          }, 100);
        }
        break;
      // FF
      case keyboard.isFastForward(e) && !ignoreVideoControls:
        if (!ignoreVideoControls) {
          this.promise = deps.$interval(() => {
            this.seekRepeat(deps.topics.playerStates.forward.name);
          }, 100);
        }
        break;
    }
  }

  playPause(e) {
    if (keyboard.isEnter(e)) {
      this.API.playPause();
    } else if (keyboard.isRight(e)) {
      angular.element(document.getElementsByTagName('vg-scrub-bar'))[0].focus();
    }
  }

  scroll(e) {
    e.preventDefault();
    if (keyboard.isLeft(e)) {
      this.seekRepeat(deps.topics.playerStates.rewind.name, true);
    } else if (keyboard.isRight(e)) {
      this.seekRepeat(deps.topics.playerStates.forward.name, true);
      deps.$timeout(() => {
        this.API.play();
      }, 100);
    } else if (keyboard.isPlayPause(e)) {
      this.currentStatus = deps.topics.playerStates.play.name;
    }
    this.API.setPlayback(1);
  }

  onComplete() {
    // go back when video completes
    this.goBack();
  }

  onPrerollComplete() {
    this.incrementPrerollCount();
  }

  goBack() {
    deps.$state.go(deps.$rootScope.previousState.name, deps.$rootScope.previousParams);
  }
}

VideoplayerController.$inject = [
  '$rootScope',
  '$scope',
  '$sce',
  '$timeout',
  '$state',
  '$interval',
  '$stateParams',
  'states',
  'navigationService',
  'localService',
  'topics',
  'storageKeys',
  'adRotationFrequency'
];

export default VideoplayerController;



/** WEBPACK FOOTER **
 ** ./client/app/components/videoplayer/videoplayer.controller.js
 **/